import chevronIconSVG from "../../icons/chevron-down.svg?raw";
import {
  animate,
  assignAttributes,
  cloneElement,
  closest,
  contains,
  createElement,
  focusElement,
  getAttributes,
  getPopup,
  replaceChildren,
  uniqueId,
} from "../utils";

/**
 * A document-level, delegated click event handler for reacting to clicks
 * outside of disclosure elements.
 *
 * The function is defined in module scope to reduce redundancy: it's meant to
 * cover all disclosure elements in the document.
 *
 * @type {null|((event: PointerEvent) => void)}
 */
let handleClickOutside = null;

/**
 * A disclosure component.
 *
 * The component is essentially a progressively enhanced `<details>` element.
 * In fact, the component expects its initial child markup to include a single
 * `<details>` element. During initialization, the content of the child
 * `<details>` element and any descendant `<summary>` element will be
 * "upgraded" as the component's content and trigger respectively.
 *
 * Like the native `<details>`, the component's state is indicated with the
 * boolean attribute `open`. The attribute is added and removed by the
 * component in response to user interaction. The component can be controlled
 * by manually adding or removing the `open` attribute, or by reassigning the
 * `open` property on the component instance (e.g.
 * `disclosureElement.open = false`).
 *
 * Also like the native `<details>`, the component emits a `toggle` event when
 * its state changes. The event is emitted immediately after the `open`
 * attribute is changed, but before any animations are begun and changes made
 * to the component's visibility.
 *
 * The component can be configured as a classic, `<details>`-style expandable
 * widget (the default), or as a "button with popup" widget that's more similar
 * to menu buttons and comboboxes. The boolean attribute `haspopup` should be
 * added for the latter behavior.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/details | <details> (MDN)}
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/ | WAI ARIA APG Disclosure Pattern}
 * @example
 * ```html
 * <!-- Default usage -->
 * <ifrs-disclosure>
 *   <details>
 *     <summary>Disclosure button text</summary>
 *     Disclosure content. Lorem ipsum...
 *   </details>
 * </ifrs-disclosure>
 *
 * <!-- Expanded content on initial render -->
 * <ifrs-disclosure>
 *   <details open>
 *     <summary>Disclosure button text</summary>
 *     <h3>This content will be visible by default, even before any user interaction.</h3>
 *     <p>Lorem ipsum...</p>
 *   </details>
 * </ifrs-disclosure>
 *
 * <!-- Disclosure content inside a "popup" -->
 * <ifrs-disclosure haspopup>
 *   <details>
 *     <summary>Menu button</summary>
 *     <strong>This content will be rendered inside a "popup" container.</strong>
 *     <ul class="menu">
 *       <!-- // ... -->
 *     </ul>
 *   </details>
 * </ifrs-disclosure>
 * ```
 */
export default class Disclosure extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-disclosure") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * Define observed attributes.
   */
  static observedAttributes = ["open"];

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup = new Set();

  /**
   * The current state of the component.
   *
   * @type {boolean}
   */
  get open() {
    return this.hasAttribute("open");
  }

  /**
   * @param {boolean} value The new state of the component.
   */
  set open(value) {
    this.toggleAttribute("open", !!value);
  }

  /**
   * The popup bundle returned by `getPopup()`.
   *
   * @type {void|ReturnType<typeof getPopup>}
   */
  popup;

  /**
   * Reacts to changes of observed attributes. The function makes DOM updates
   * to reflect the current state of the component.
   *
   * @param {string} name The changed attribute name
   * @param {*} prev The previous value of the attribute
   * @param {*} next The new value of the attribute
   */
  async attributeChangedCallback(name, prev, next) {
    // Toggle component state when the `open` attribute changes.
    if (name === "open") {
      const button = this.querySelector(".disclosure-item-button");
      const content = this.querySelector(".disclosure-item-content");
      const container = this.querySelector(".disclosure-item-container");

      // Abort if we don't have the expected markup.
      if (
        !(button instanceof HTMLElement) ||
        !(container instanceof HTMLElement) ||
        !(content instanceof HTMLElement)
      ) {
        return;
      }

      // Dispatch a "toggle" event before making any changes.
      this.dispatchEvent(new Event("toggle", { bubbles: true, cancelable: true }));

      // Get the new state of the component.
      const isExpanded = next !== null;

      // Set the `aria-expanded` attribute.
      button.setAttribute("aria-expanded", isExpanded.toString());

      // Toggle "popup" component state.
      if (this.hasAttribute("haspopup")) {
        if (isExpanded) {
          // Un-hide the container element.
          container.hidden = false;
          // Set up Floating UI behavior.
          this.popup = getPopup({
            reference: button,
            floating: container,
            arrow: this.querySelector(".disclosure-item-caret"),
          });
          // Trigger the Floating UI logic.
          this.popup.render();
          // Clean up Floating UI side effects.
          this.cleanup.add(() => {
            this.popup = this.popup && this.popup.cleanup();
          });
        }

        // Animate the content in/out
        await animate(container, [
          {
            opacity: isExpanded ? 1 : 0,
            transform: `translateY(${isExpanded ? "0" : "1rem"})`,
          },
        ]);

        // On collapse, hide the content once the animation has completed.
        if (!this.open) {
          // Hide the container element.
          container.hidden = true;
          // Clean up Floating UI side effects.
          this.popup = this.popup && this.popup.cleanup();
        }
      }

      // Toggle non-popup component state.
      if (!this.hasAttribute("haspopup")) {
        container.style.overflow = "hidden";
        content.style.visibility = null;
        await animate(container, [{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }]);
        container.style.overflow = isExpanded ? null : "hidden";
        content.style.visibility = isExpanded ? null : "hidden";
      }
    }
  }

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    // Rerun setup when component children change.
    const observer = new MutationObserver(() => this.setup());
    observer.observe(this, { childList: true });
    this.cleanup.add(() => observer.disconnect());

    // Define the global, "click outside" handler.
    if (!handleClickOutside) {
      /**
       * Collapse all open, "popup" disclosure elements when clicks occur
       * elsewhere in the document.
       *
       * @param {PointerEvent} event A PointerEvent object.
       */
      handleClickOutside = (event) => {
        const current = closest(event.target, "ifrs-disclosure");
        const elements = document.querySelectorAll("ifrs-disclosure[haspopup]");
        if (elements.length < 1) {
          // If no (popup) disclosure elements exist, remove the handler.
          document.removeEventListener("click", handleClickOutside);
        } else {
          elements.forEach((el) => {
            if (el !== current && el instanceof Disclosure && el.open) {
              el.open = false;
            }
          });
        }
      };
    }

    // Add the "click outside" event handler.
    document.addEventListener("click", handleClickOutside);

    /**
     * Toggle the component state on button click events.
     *
     * @param {PointerEvent} event A PointerEvent object.
     */
    const handleClick = (event) => {
      if (closest(event.target, ".disclosure-item-button")) {
        this.open = !this.open;
      }
    };

    // Add the click event handler.
    this.addEventListener("click", handleClick);
    this.cleanup.add(() => this.removeEventListener("click", handleClick));

    /**
     * Collapse the component when it loses focus (for components with popups).
     *
     * @param {FocusEvent} event A FocusEvent object
     */
    const handleFocusOut = (event) => {
      if (this.hasAttribute("haspopup") && contains(this, event.relatedTarget)) {
        // this.open = false;
      }
    };

    // Add the focus out handler.
    this.addEventListener("focusout", handleFocusOut);
    this.cleanup.add(() => this.removeEventListener("focusout", handleFocusOut));

    /**
     * Collapse the component when the "Escape" key is pressed (for components
     * with popups).
     *
     * @param {KeyboardEvent} event A KeyboardEvent object.
     */
    const handleKeyDown = (event) => {
      if (this.hasAttribute("haspopup") && event.key === "Escape") {
        this.open = false;
        focusElement(this.querySelector(".disclosure-item-button"));
      }
    };

    // Add the Escape key press handler.
    this.addEventListener("keydown", handleKeyDown);
    this.cleanup.add(() => this.removeEventListener("keydown", handleKeyDown));
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Set up the component markup.
   *
   * Note that this method may be called repeatedly and should be idempotent.
   */
  setup() {
    // Abort if the component markup has already been set up.
    if (this.children.length > 0 && this.firstElementChild.matches(".disclosure-item-button")) {
      return;
    }

    // Get the first descendant `<details>` element.
    const details = this.querySelector("details");

    // Abort if no child details element exists.
    if (!details) {
      return;
    }

    // Copy attributes from the details element.
    assignAttributes(this, getAttributes(details));

    // Get the descendant `<summary>` element.
    let summary = details.querySelector("summary");

    if (summary) {
      // Remove the summary element if it exists. This lets us easily reference
      // all other child nodes of the details element.
      summary.remove();
    } else {
      // Create a default summary element if none exists.
      summary = createElement("summary", { textContent: "Details" });
    }

    // Create a unique id for aria purposes.
    const id = uniqueId(this.tagName);

    // Get the current component state.
    const isExpanded = details.open || this.hasAttribute("open");

    /**
     * The component trigger button.
     */
    const button = createElement(
      "button",
      {
        ...getAttributes(summary),
        "aria-controls": id,
        "aria-expanded": isExpanded ? "true" : "false",
        id: `${id}-button`,
        classList: "disclosure-item-button",
        type: "button",
      },
      [
        ...summary.childNodes,
        cloneElement(chevronIconSVG, {
          classList: "icon disclosure-item-icon",
          xmlns: null,
        }),
      ],
    );

    /**
     * The component content.
     */
    const content = createElement(
      "div",
      { classList: "disclosure-item-content", id },
      details.childNodes,
    );

    /**
     * A content container included for styling concerns.
     */
    const container = createElement(
      "div",
      { classList: "disclosure-item-container", tabIndex: -1 },
      content,
    );

    // Apply additional changes for "popup" components.
    if (this.hasAttribute("haspopup")) {
      assignAttributes(container, {
        hidden: true,
        style: { opacity: 0, transform: "translateY(1rem)" },
      });
      content.prepend(createElement("span", { classList: "disclosure-item-caret" }));
    }

    // Apply additional changes for non-popup components.
    if (!this.hasAttribute("haspopup")) {
      assignAttributes(content, {
        "aria-labelledby": `${id}-button`,
        role: "region",
        style: {
          minHeight: 0,
          visibility: isExpanded ? null : "hidden",
        },
      });
      assignAttributes(container, {
        style: {
          display: "grid",
          gridTemplateRows: isExpanded ? "1fr" : "0fr",
          overflow: isExpanded ? null : "hidden",
        },
      });
    }

    // Replace the component content with our new markup.
    replaceChildren(this, button, container);

    this.open = isExpanded;
  }
}
